import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

api.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    return Promise.reject(err.response?.data || err);
  }
);

export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  check: () => api.get('/auth/check'),
};

export const configApi = {
  get: () => api.get('/config'),
};

export const environmentsApi = {
  list: () => api.get('/environments'),
  get: id => api.get(`/environments/${id}`),
  update: (id, data) => api.put(`/environments/${id}`, data),
};

export const reposApi = {
  list: () => api.get('/repos'),
  listGcpTriggers: (repo) => api.get('/repos/gcp-triggers', { params: repo ? { repo } : {} }),
  getDependencies: id => api.get(`/repos/${id}/dependencies`),
  setDependencies: (id, dependsOn) => api.put(`/repos/${id}/dependencies`, { dependsOn }),
  dependencyGraph: () => api.get('/repos/dependency-graph'),
};

export const pipelinesApi = {
  list: () => api.get('/triggers'),
  get: id => api.get(`/triggers/${id}`),
  create: data => api.post('/triggers', data),
  update: (id, data) => api.put(`/triggers/${id}`, data),
  delete: id => api.delete(`/triggers/${id}`),
  trigger: (id, data) => api.post(`/triggers/${id}/trigger`, data),
  getCloudBuildConfig: (repo, component, triggerName) =>
    api.get('/triggers/cloudbuild-config', {
      params: triggerName ? { triggerName } : component ? { repo, component } : { repo },
    }),
  triggerDirect: (data) => api.post('/triggers/trigger-build', data),
  envCheck: (data) => api.post('/triggers/env-check', data),
  createSecrets: (data) => api.post('/triggers/update-secrets', data),
  listSecrets: () => api.get('/triggers/secrets'),
  setEnvConfig: (data) => api.put('/triggers/env-config', data),
  getDependencies: id => api.get(`/triggers/${id}/dependencies`),
  setDependencies: (id, dependsOn) => api.put(`/triggers/${id}/dependencies`, { dependsOn }),
  executionPlan: () => api.get('/triggers/execution-plan'),
};

export const buildsApi = {
  list: params => api.get('/builds', { params }),
  get: (buildId) => api.get(`/builds/${buildId}`),
  getLogs: (buildId) => api.get(`/builds/${buildId}/logs`),
  cancel: (buildId) => api.post(`/builds/${buildId}/cancel`),
  retry: (buildId) => api.post(`/builds/${buildId}/retry`),
};

export const workflowRunsApi = {
  create: (branch) => api.post('/workflow-runs', { branch }),
  latest: () => api.get('/workflow-runs/latest'),
  list: (limit) => api.get('/workflow-runs', { params: limit ? { limit } : {} }),
  draft: () => api.get('/workflow-runs/draft'),
  setDraftTrigger: (repoId, triggerName) => api.put(`/workflow-runs/draft/repos/${repoId}/trigger`, { triggerName }),
  retryRepo: (runId, repoId) => api.post(`/workflow-runs/${runId}/retry-repo`, { repoId }),
};

export const servicesApi = {
  list: params => api.get('/services', { params }),
  allEnvironments: () => api.get('/services/all-environments'),
  get: (name, params) => api.get(`/services/${name}`, { params }),
  revisions: (name, params) => api.get(`/services/${name}/revisions`, { params }),
  updateTraffic: (name, data) => api.post(`/services/${name}/traffic`, data),
};

export const deploymentsApi = {
  list: params => api.get('/deployments', { params }),
  rollback: data => api.post('/deployments/rollback', data),
};

export const artifactsApi = {
  listImages: params => api.get('/artifacts/images', { params }),
  listTags: (image, params) => api.get(`/artifacts/images/${image}/tags`, { params }),
};

export function createBuildWs(buildId) {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return new WebSocket(`${protocol}//${location.host}/ws/builds?buildId=${buildId}`);
}

export default api;
